import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as store from '../lib/racetimeStore';
import StartsPasteImport from '../components/StartsPasteImport';
import RaceDayOverrideSection from '../components/RaceDayOverrideSection';
import { COURSE_CARD_SENTINELS, isSentinel, sentinelLabel } from '../lib/courseCardSentinels';

const noop = () => {};
const DAY_ORDER = ['saturday', 'thursday', 'tuesday', 'sunday', 'monday', 'wednesday', 'friday'];
const LOC_ABBREV = { hut: 'Hut', corinthian: 'Cor', freebird: 'FB', coastal: 'Cst', west_pier: 'WP' };

function locAbbrev(loc) { return LOC_ABBREV[loc] || (loc || '').substring(0, 3); }
function getClasses(s) { return s.classes || (s.class_id ? [s.class_id] : []); }
function getFlags(s) { return s.flags || (s.flag ? [s.flag] : []); }
function formatShortDate(iso) {
  const [, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`;
}
function className(classId, classes) {
  const c = classes.find((cl) => cl.id === classId);
  return c ? c.name : classId;
}

function deriveStartTime(warningHHMM, sequenceMinutes) {
  return store.addMinutesToHHMM(warningHHMM, sequenceMinutes);
}

// Course-card display helpers (ra-yuuv): variants store a course_card_id (a GUID
// or an x_ sentinel), but the UI must show the human-readable NAME. courseCards
// is [{ id, name, source: 'active'|'preview'|'draft', version }].
function cardNameById(courseCards, id) {
  if (!id) return null;
  const cc = (courseCards || []).find((c) => c.id === id);
  if (cc) return cc.name;
  return sentinelLabel(id); // x_ sentinel → its label; otherwise null (unknown id)
}

// The source a card comes from, rendered for a human (ra-29i6i). A card from the
// PREVIEW bundle is selectable but is not live for sailors, and an operator must
// be able to see that at the moment of picking — and afterwards, in the row.
function cardSourceNote(cc) {
  if (!cc) return null;
  if (cc.source === 'preview') return `preview v${cc.version}`;
  if (cc.source === 'draft') return 'draft';
  return null; // active — the unremarkable case, no note
}
function cardSourceNoteById(courseCards, id) {
  return cardSourceNote((courseCards || []).find((c) => c.id === id));
}

// ── Course-card write policy (ra-yuuv, split in ra-29i6i) ──────────────
//
// TWO write paths make a course_card_id live: the templated Publish Schedule,
// and the Simple editor's per-date Save (which writes race_schedule/{date}
// directly and is live the instant it lands). Both must apply the SAME policy,
// so it lives here rather than inside either handler — a guard on one branch is
// not a guard, and the direct path is the MORE dangerous of the two because
// there is no publish step between the operator and the sailors.
//
//   active           → resolvable for everyone. Fine.
//   preview-only     → resolvable for a reviewer who has loaded the preview
//                      bundle, BLANK for sailors on the active one until that
//                      version is activated. Allowed — scheduling the card is
//                      exactly what makes it previewable — but never silently.
//   draft / unknown  → resolvable by NOBODY, preview mode included. Refused.
//
// Sentinels (x_*) and an empty id both mean "no digital card" and are valid.
function classifyCardRef(id, courseCards) {
  if (!id || isSentinel(id)) return 'ok';
  const source = (courseCards || []).find((cc) => cc.id === id)?.source || null;
  if (source === 'active') return 'ok';
  if (source === 'preview') return 'preview';
  return 'unresolvable';
}

/** Split [{ label, cardId }] into the labels that must block vs merely warn. */
function auditCardRefs(entries, courseCards) {
  const blocked = [];
  const preview = [];
  for (const { label, cardId } of entries) {
    const verdict = classifyCardRef(cardId, courseCards);
    if (verdict === 'unresolvable') blocked.push(label);
    else if (verdict === 'preview') preview.push(label);
  }
  return { blocked, preview };
}

function blockedCardMessage(labels) {
  return `${labels.join(', ')} reference${labels.length === 1 ? 's a' : ''} course card that isn't in any published bundle (draft, removed, or unknown). Publish the course bundle on the Course Cards page first.`;
}

function previewCardWarning(labels, bundleVersions, whatHappens) {
  const previewVersion = bundleVersions?.preview ?? null;
  const activeVersion = bundleVersions?.active ?? null;
  const previewLabel = previewVersion != null ? `preview bundle v${previewVersion}` : 'the preview bundle';
  const activateLabel = previewVersion != null ? `v${previewVersion}` : 'that version';
  // activeVersion null means no bundle is activated for this event at all — say
  // that, rather than implying there is an active bundle the card is missing from.
  const notLive = activeVersion != null
    ? `which is NOT in the active bundle (v${activeVersion})`
    : 'and NO bundle is activated for this event yet';
  const sailorsSee = activeVersion != null
    ? 'Sailors on the active bundle will see NO course for those classes'
    : 'Sailors will see NO course for those classes';
  return (
    `${labels.join(', ')} reference${labels.length === 1 ? 's a' : ''} course card from ${previewLabel}, ${notLive}.\n\n` +
    `Reviewers loading the preview bundle in the app will see the course. ${sailorsSee} until ${activateLabel} is activated.\n\n` +
    whatHappens
  );
}

/** Small "preview v19" / "draft" tag rendered beside a chosen card's name. */
function CardSourceBadge({ note }) {
  if (!note) return null;
  return (
    <span style={{ marginLeft: 4, fontSize: '.65rem', color: '#8a5a00', whiteSpace: 'nowrap' }}>
      ({note})
    </span>
  );
}

// Shared <select> for picking a course card by name while storing its id.
//
// Grouped BY SOURCE so the bundle a card comes from is on the face of the
// option rather than in a tooltip (ra-29i6i):
//   Active bundle   — what sailors resolve today. Selectable.
//   Preview bundle  — a published version pinned as the app preview. Selectable:
//                     this is the whole point of the fix. Scheduling a preview
//                     card is what makes it reachable in the app's preview mode,
//                     because the app filters bundle cards by the schedule's
//                     course_card_id — an unscheduled card is invisible even to
//                     a global admin loading the preview bundle.
//   Draft           — wip/vNext only. Shown so a new card doesn't look missing,
//                     but DISABLED: it is in no published bundle, so no app can
//                     resolve it, preview mode included (ra-yuuv).
//   Sentinels       — the x_ "no digital card" values.
// Preserves a current value that isn't in any list (legacy/removed card) so
// switching to a select never silently blanks an existing assignment.
function CourseCardSelect({ value, onChange, courseCards, style }) {
  const cards = courseCards || [];
  const known = cards.some((cc) => cc.id === value) ||
    COURSE_CARD_SENTINELS.some((s) => s.id === value);
  const bySource = (source) => cards.filter((cc) => cc.source === source);
  const active = bySource('active');
  const preview = bySource('preview');
  const draft = bySource('draft');
  const versionOf = (list) => (list.length && list[0].version != null ? ` (v${list[0].version})` : '');
  return (
    <select value={value || ''} onChange={onChange} style={style}>
      <option value="">— none —</option>
      {active.length > 0 && (
        <optgroup label={`Active bundle${versionOf(active)}`}>
          {active.map((cc) => <option key={cc.id} value={cc.id}>{cc.name}</option>)}
        </optgroup>
      )}
      {preview.length > 0 && (
        <optgroup label={`Preview bundle${versionOf(preview)} — not yet live for sailors`}>
          {preview.map((cc) => (
            <option key={cc.id} value={cc.id}>{`${cc.name} (preview v${cc.version})`}</option>
          ))}
        </optgroup>
      )}
      {draft.length > 0 && (
        <optgroup label="Draft — publish the bundle first">
          {draft.map((cc) => (
            <option key={cc.id} value={cc.id} disabled>{`${cc.name} (draft — publish bundle first)`}</option>
          ))}
        </optgroup>
      )}
      <optgroup label="No digital card (sentinels)">
        {COURSE_CARD_SENTINELS.map((s) => (
          <option key={s.id} value={s.id}>{s.label}</option>
        ))}
      </optgroup>
      {value && !known && <option value={value}>{value} (not in bundle)</option>}
    </select>
  );
}

export default function Racetimes() {
  const { user, isGlobalAdmin } = useAuth();
  const params = useParams();
  const fixedClubId = params.clubId || null; // set when accessed via /clubs/:clubId/racetimes
  const [clubs, setClubs] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [clubId, setClubId] = useState(fixedClubId || '');
  const [seasonId, setSeasonId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [changeLog, setChangeLog] = useState([]);

  // Data
  const [variants, setVariants] = useState({});
  const [rotation, setRotation] = useState(null);
  const [raceDates, setRaceDates] = useState([]);
  const [availClasses, setAvailClasses] = useState([]);
  const [courseCards, setCourseCards] = useState([]); // [{ id, name, source, version }]
  // { active, preview } bundle version numbers, straight from the event doc.
  // Read here rather than inferred from courseCards: an empty active bundle has
  // no card to carry its version, and the publish warning needs to be able to
  // say "not in the active bundle (v18)" in exactly that case (ra-29i6i).
  const [bundleVersions, setBundleVersions] = useState({ active: null, preview: null });

  // Expanded variant
  const [expanded, setExpanded] = useState(null);

  // Add variant form. The form renders at the page BOTTOM, below the (often long)
  // variants/rotation/matrix sections, so when variants already exist clicking
  // "+ Add Variant" opened it far below the fold → looked like a no-op (ra-yuuv).
  // Scroll it into view + focus its first field on open so the action is visibly
  // surfaced regardless of how much content sits above it.
  const [showAddForm, setShowAddForm] = useState(false);
  const addFormRef = useRef(null);

  useEffect(() => {
    if (showAddForm && addFormRef.current) {
      addFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showAddForm]);
  const [newVariant, setNewVariant] = useState({ id: '', name: '', day: 'saturday', location: '', vhf: '', fleet: '', course_card_id: '', document_set_id: '' });

  // Add race date form
  const [newRaceDate, setNewRaceDate] = useState({ date: '', status: 'on_schedule', delay: 0, variants: '', notes: '' });

  // Publishing
  const [publishing, setPublishing] = useState(false);
  const [publishMsg, setPublishMsg] = useState(null); // { type: 'error'|'success', text }

  // Variant form error
  const [variantFormError, setVariantFormError] = useState('');

  // Race date form error
  const [raceDateError, setRaceDateError] = useState('');

  // ── Racetimes redesign (ra-1kga) ──
  // Per-event authoring source: 'templated' (variants + rotation, DRY) or
  // 'simple' (direct per-date). Persisted on the event doc.
  const [authoringMode, setAuthoringMode] = useState('templated');
  // Series default active-race window from the event doc (ra-8en2.1.4). Read-only
  // here — there is no editor yet — but it must be held and passed back into
  // publish, or publishing would strip a window the series had declared.
  const [seriesWindow, setSeriesWindow] = useState(null);
  // Published schedule (race_schedule/*) lifted to the page: it feeds BOTH the
  // top view-only matrix (overview + drill-down) and the bottom summary.
  const [publishedDates, setPublishedDates] = useState([]);
  const [publishedIndex, setPublishedIndex] = useState(null);
  const [pubLoading, setPubLoading] = useState(true);
  const [pubErr, setPubErr] = useState(null);
  // Matrix drill-down: the single date whose read-only published view is open.
  const [selectedDate, setSelectedDate] = useState(null);

  const logChange = useCallback((msg) => {
    setChangeLog((prev) => [...prev, { time: new Date().toLocaleTimeString(), msg }]);
  }, []);

  // Load clubs (skip if club-scoped)
  useEffect(() => {
    if (fixedClubId) {
      setClubs([{ id: fixedClubId, name: fixedClubId }]);
      setClubId(fixedClubId);
      setLoading(false);
      return;
    }
    store.loadClubsWithEvents()
      .then((c) => { setClubs(c); if (c.length) setClubId(c[0].id); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [fixedClubId]);

  // Load seasons when club changes
  useEffect(() => {
    if (!clubId) return;
    store.loadSeasons(clubId).then((s) => {
      s.sort((a, b) => (b.id > a.id ? 1 : -1));
      setSeasons(s);
      if (s.length) setSeasonId(s[0].id);
      else setSeasonId('');
    });
  }, [clubId]);

  // Load racetime data when club/season changes
  useEffect(() => {
    if (!clubId || !seasonId) return;
    setLoading(true);
    setError(null);
    store.loadRacetimeData(clubId, seasonId)
      .then((data) => {
        setVariants(data.variants);
        setRotation(data.rotation);
        setRaceDates(data.raceDates);
        setAvailClasses(data.classes);
        setCourseCards(data.courseCards);
        setBundleVersions(data.bundleVersions || { active: null, preview: null });
        setAuthoringMode(data.authoringMode || 'templated');
        setSeriesWindow(data.activeWindow || null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [clubId, seasonId]);

  const reload = useCallback(() => {
    if (!clubId || !seasonId) return;
    store.loadRacetimeData(clubId, seasonId).then((data) => {
      setVariants(data.variants);
      setRotation(data.rotation);
      setRaceDates(data.raceDates);
      setAvailClasses(data.classes);
      setCourseCards(data.courseCards);
      setBundleVersions(data.bundleVersions || { active: null, preview: null });
      setAuthoringMode(data.authoringMode || 'templated');
      setSeriesWindow(data.activeWindow || null);
    });
  }, [clubId, seasonId]);

  // Load the published schedule (race_schedule/*) — the live docs the app reads.
  // Lifted to the page so the top matrix and bottom summary share one source.
  const reloadPublished = useCallback(() => {
    if (!clubId || !seasonId) return;
    setPubLoading(true);
    store.loadPublishedSchedule(clubId, seasonId)
      .then((d) => { setPublishedDates(d.dates); setPublishedIndex(d.index); setPubErr(null); })
      .catch((e) => setPubErr(e.message))
      .finally(() => setPubLoading(false));
  }, [clubId, seasonId]);

  useEffect(() => { reloadPublished(); }, [reloadPublished]);

  // Switch authoring source and persist the choice on the event doc (ra-1kga).
  // Switching source is a SERIOUS, data-losing change: each source's Publish
  // REGENERATES race_schedule/* and silently overwrites whatever the OTHER source
  // authored. Chris asked (PR #759) that switching be "much harder than accidental"
  // because it involves loss of data — so gate it behind a TYPED confirmation (the
  // user must type the target source name), not a single-click OK. Compare against
  // effectiveMode, not the stored authoringMode: a locked templated event may hold a
  // stale 'simple' preference, and clicking the (forced) Templated option there is an
  // alignment no-op that must not pop a data-loss prompt.
  async function handleChangeAuthoringMode(mode) {
    if (mode === effectiveMode) return;
    const target = mode === 'simple' ? 'Simple' : 'Templated';
    const other = mode === 'simple' ? 'Templated' : 'Simple';
    const typed = window.prompt(
      `Switch authoring source to ${target}?\n\n`
      + `This is a serious change. Publishing from ${target} REGENERATES the race `
      + `schedule and can permanently overwrite what the ${other} source authored — `
      + `this cannot be undone.\n\n`
      + `Type ${target.toUpperCase()} to confirm the switch.`,
    );
    if (!typed || typed.trim().toUpperCase() !== target.toUpperCase()) return;
    setAuthoringMode(mode);
    try {
      await store.saveAuthoringMode(clubId, seasonId, mode);
      logChange(`Authoring source switched to ${mode}`);
    } catch (e) {
      setError(`Could not save authoring mode: ${e.message}`);
    }
  }

  // ── Simple (direct per-date) authoring — edits race_schedule/* live ──
  //
  // This write is live the instant it lands — there is no publish step between
  // it and the sailors — so it needs the SAME course-card policy the templated
  // Publish applies, not a weaker one (codex, ra-29i6i). Before ra-29i6i this
  // path had no card guard at all: even a draft card could be saved straight
  // onto the live schedule from here.
  async function handleSavePublishedDate(date, payload) {
    // Audit only the cards this save CHANGES, not every card already sitting in
    // the live doc. A published date can legitimately reference a card that is
    // in no current list — removed by a later bundle, or a legacy id — and
    // auditing the whole document would refuse an unrelated edit (a start time,
    // a VHF channel) that the operator has no way to make satisfiable. On a
    // live race-day surface that is worse than the bug this guards against.
    // Judge the delta; leave untouched history alone.
    const publishedLocs = publishedDates.find((d) => d.date === date)?.locations || {};
    const changedCards = Object.entries(payload?.locations || {})
      .filter(([locId, loc]) => (loc?.course_card_id || null) !== (publishedLocs[locId]?.course_card_id || null))
      .map(([locId, loc]) => ({ label: loc?.name || locId, cardId: loc?.course_card_id }));
    const { blocked, preview } = auditCardRefs(changedCards, courseCards);
    if (blocked.length) {
      alert(`Cannot save ${date}: ${blockedCardMessage(blocked)}`);
      return;
    }
    if (preview.length && !confirm(previewCardWarning(
      preview,
      bundleVersions,
      `Save ${date} to the live schedule anyway?`,
    ))) return;
    await store.savePublishedScheduleDate(clubId, seasonId, date, payload, user?.email);
    logChange(`Saved published schedule for ${date}`);
    reloadPublished();
  }
  async function handleDeletePublishedDate(date) {
    if (!confirm(`Delete the published schedule for ${date}? The app will no longer show races for this date.`)) return;
    await store.deletePublishedScheduleDate(clubId, seasonId, date, user?.email);
    logChange(`Deleted published schedule for ${date}`);
    if (selectedDate === date) setSelectedDate(null);
    reloadPublished();
  }
  async function handleAddPublishedDate(date) {
    await store.savePublishedScheduleDate(clubId, seasonId, date, { day: dayNameForDate(date), locations: {} }, user?.email);
    logChange(`Created published schedule date ${date}`);
    reloadPublished();
  }

  // ── Race-day overrides (ra-53j9) — LIVE, by design ──
  // The RO learns at 11:00 that the committee boat has changed and the gun is
  // at 14:10; a publish-gated override would be correct and useless. The store
  // dual-writes: race_dates/{date} (durable, so the next Publish reproduces it)
  // + a PATCH of the live race_schedule/{date} the app reads.
  async function handleApplyRaceDayOverride(date, payload) {
    const result = await store.saveRaceDayOverride(clubId, seasonId, date, payload);
    logChange(`Race-day override applied for ${date}${result.live ? ' (live)' : ' (stored — not published yet)'}`);
    reload();
    reloadPublished();
    return result;
  }
  async function handleClearRaceDayOverride(date) {
    // Restore each location's SERIES identity by RE-DERIVING this date without
    // its override — the published doc has been overwritten and can't remember
    // what the name/VHF/day were. Derived per date, not by scanning variants
    // for a matching location: DBSC has both a Saturday and a Thursday variant
    // on `freebird`, and a first-match scan would restore the Saturday fleet's
    // name and channel into a Thursday schedule. Simple (variant-less) events
    // derive nothing: the vessel is dropped and name/VHF stay as published.
    const withoutOverride = raceDates.map((rd) => (
      (rd.date || rd._id) === date ? { ...rd, location_overrides: null, day: null } : rd
    ));
    const derived = store.deriveScheduleDates(variants, rotation, withoutOverride, { activeWindow: seriesWindow });
    const seriesDate = derived.find((d) => d.date === date);
    const defaults = { day: seriesDate?.day, locations: {} };
    Object.entries(seriesDate?.locations || {}).forEach(([locId, loc]) => {
      defaults.locations[locId] = { name: loc.name, vhf: loc.vhf ?? null };
    });
    const result = await store.clearRaceDayOverride(clubId, seasonId, date, defaults);
    logChange(`Race-day override cleared for ${date}`);
    reload();
    reloadPublished();
    return result;
  }

  // Simple (direct) path Publish: per-date saves are already live, so this is an
  // explicit confirm that re-stamps the published index metadata — mirroring the
  // Templated path's Publish so both modes have a visible Publish step (ra-c5r7).
  async function handlePublishSimple() {
    if (!publishedDates.length) {
      setPublishMsg({ type: 'error', text: 'Nothing to publish — add at least one date first.' }); return;
    }
    if (!confirm(`Publish the race schedule for ${publishedDates.length} date${publishedDates.length === 1 ? '' : 's'}?`)) return;
    setPublishing(true);
    setPublishMsg(null);
    try {
      const count = await store.republishSchedule(clubId, seasonId, user?.email);
      logChange(`Published race schedule (Simple): ${count} dates`);
      setPublishMsg({ type: 'success', text: `Published ${count} race schedule documents.` });
      reloadPublished();
    } catch (e) {
      setPublishMsg({ type: 'error', text: `Publish failed: ${e.message}` });
    } finally {
      setPublishing(false);
    }
  }

  // ── Variant operations ──
  async function handleSaveVariantMeta(vid, data) {
    await store.saveVariantMeta(clubId, seasonId, vid, data);
    setVariants((prev) => ({ ...prev, [vid]: { ...prev[vid], ...data } }));
    logChange(`Updated metadata for ${vid}`);
  }

  async function handleSaveStarts(vid, starts) {
    await store.saveVariantStarts(clubId, seasonId, vid, starts);
    setVariants((prev) => ({ ...prev, [vid]: { ...prev[vid], starts } }));
    logChange(`Saved ${starts.length} starts for ${vid}`);
  }

  async function handleDeleteVariant(vid) {
    if (!confirm(`Delete variant "${vid}"? This cannot be undone.`)) return;
    await store.deleteVariant(clubId, seasonId, vid);
    setVariants((prev) => { const next = { ...prev }; delete next[vid]; return next; });
    logChange(`Deleted variant ${vid}`);
  }

  async function handleCreateVariant() {
    const { id: vid, name, day, location, vhf, fleet, course_card_id, document_set_id } = newVariant;
    if (!vid || !name) { setVariantFormError('Variant ID and Name are required.'); return; }
    if (!/^[a-z0-9_]+$/.test(vid)) { setVariantFormError('Variant ID must be lowercase letters, numbers, and underscores.'); return; }
    if (variants[vid]) { setVariantFormError(`Variant "${vid}" already exists.`); return; }
    setVariantFormError('');
    const data = { name, day, starts: [] };
    if (location) data.location = location;
    if (vhf) data.vhf = vhf;
    if (fleet) data.fleet = fleet;
    if (course_card_id) data.course_card_id = course_card_id;
    if (document_set_id) data.document_set_id = document_set_id;
    await store.createVariant(clubId, seasonId, vid, data);
    setVariants((prev) => ({ ...prev, [vid]: data }));
    setShowAddForm(false);
    setNewVariant({ id: '', name: '', day: 'saturday', location: '', vhf: '', fleet: '', course_card_id: '', document_set_id: '' });
    logChange(`Created variant ${vid}`);
  }

  // ── Rotation operations ──
  async function handleSaveRotation(newRotation) {
    await store.saveRotation(clubId, seasonId, newRotation);
    setRotation(newRotation);
    logChange('Updated rotation schedule');
  }

  // ── Race date operations ──
  async function handleAddRaceDate() {
    const { date, status, delay, variants: varStr, notes } = newRaceDate;
    if (!date) { setRaceDateError('Date is required'); return; }
    setRaceDateError('');
    const data = { date, status, notes: notes || null };
    if (parseInt(delay, 10) > 0) data.delay_minutes = parseInt(delay, 10);
    if (varStr.trim()) {
      data.override_variants = varStr.split(',').map((s) => s.trim()).filter(Boolean);
    }
    data.updated_by = user?.email || null;
    await store.addRaceDate(clubId, seasonId, date, data);
    setRaceDates((prev) => [...prev, { _id: date, ...data }]);
    setNewRaceDate({ date: '', status: 'on_schedule', delay: 0, variants: '', notes: '' });
    logChange(`Added override for ${date}: ${status}`);
  }

  async function handleDeleteRaceDate(dateId) {
    if (!confirm(`Delete override for ${dateId}?`)) return;
    await store.deleteRaceDate(clubId, seasonId, dateId);
    setRaceDates((prev) => prev.filter((rd) => rd._id !== dateId));
    logChange(`Deleted override for ${dateId}`);
  }

  // ── Publish ──
  async function handlePublish() {
    if (!rotation || !Object.keys(variants).length) {
      setPublishMsg({ type: 'error', text: 'No rotation or variants to publish.' }); return;
    }
    // Check only the variants the rotation actually schedules (cancelled and
    // abandoned dates excluded), mirroring publishSchedule's selection. Policy
    // itself lives in classifyCardRef/auditCardRefs so this path and the direct
    // Save Date path cannot drift apart.
    {
      const overrideByDate = {};
      raceDates.forEach((rd) => { overrideByDate[rd.date || rd._id] = rd; });
      const scheduledVids = new Set();
      Object.values(rotation).forEach((dayData) => {
        (dayData.schedule || []).forEach((block) => {
          (block.dates || []).forEach((date) => {
            const ov = overrideByDate[date];
            if (ov && (ov.status === 'cancelled' || ov.status === 'abandoned')) return;
            const vids = ov?.override_variants?.length ? ov.override_variants : (block.variants || []);
            vids.forEach((vid) => scheduledVids.add(vid));
          });
        });
      });
      const { blocked, preview } = auditCardRefs(
        [...scheduledVids].map((vid) => ({ label: vid, cardId: variants[vid]?.course_card_id })),
        courseCards,
      );
      if (blocked.length) {
        setPublishMsg({ type: 'error', text: `Cannot publish: ${blockedCardMessage(blocked)} Then publish the schedule.` });
        return;
      }
      if (preview.length && !confirm(previewCardWarning(preview, bundleVersions, 'Publish the schedule anyway?'))) return;
    }
    const allDates = [];
    Object.values(rotation).forEach((dayData) => {
      (dayData.schedule || []).forEach((block) => { allDates.push(...(block.dates || [])); });
    });
    if (!confirm(`Publish race schedule for ${allDates.length} dates?`)) return;
    setPublishing(true);
    setPublishMsg(null);
    try {
      const count = await store.publishSchedule(clubId, seasonId, variants, rotation, raceDates, user?.email, { activeWindow: seriesWindow });
      logChange(`Published race schedule: ${count} dates`);
      setPublishMsg({ type: 'success', text: `Published ${count} race schedule documents.` });
      reloadPublished();
    } catch (e) {
      setPublishMsg({ type: 'error', text: `Publish failed: ${e.message}` });
    } finally {
      setPublishing(false);
    }
  }

  // ── Racetimes coexist fix (ra-1kga) ──
  // An event that HAS templated content (variants or a rotation) is a DRY/templated
  // event (DBSC-style) and must ALWAYS present the mature Variants + Rotation editor.
  // The #723 either/or picker HID that mature builder whenever the stored authoring
  // mode was 'simple' — that is the regression Chris reported (".25 REMOVED the mature
  // variants+rotation editor"). We derive an EFFECTIVE mode that forces 'templated'
  // whenever templated content exists, so the DRY builder can never be replaced by the
  // direct per-date editor for such events. The Simple (direct per-date) path stays
  // available only for events with NO templated content (Cork Week / one-off regattas).
  // Keeping them mutually exclusive per event also avoids a data footgun: the Templated
  // Publish REGENERATES race_schedule/* from variants + rotation and would silently
  // clobber any Simple direct edits. `authoringMode` remains the user's stored
  // preference; `effectiveMode` only guarantees the builder is never lost.
  const hasTemplatedContent = Object.keys(variants).length > 0 ||
    !!(rotation && Object.keys(rotation).length > 0);
  const effectiveMode = hasTemplatedContent ? 'templated' : authoringMode;

  // ── Season Summary (matrix) source: published vs DRAFT ──
  // The matrix normally mirrors the PUBLISHED schedule (what the app reads). In
  // edit mode it instead shows the DRAFT — the user's unpublished changes — so
  // toggling Edit flips Section 1 between live and draft and you can see, at a
  // glance, what Publish would change (ra-c5r7). Only the Templated path has a
  // draft distinct from published (it's built from variants + rotation and not
  // yet generated); Simple edits write race_schedule/* live, so its "draft" IS
  // the published schedule and the matrix is unchanged by edit mode.
  const showDraftMatrix = editMode && effectiveMode === 'templated';
  const matrixDates = showDraftMatrix
    ? store.deriveScheduleDates(variants, rotation, raceDates, { activeWindow: seriesWindow })
    : publishedDates;

  // ── Stats ──
  const variantCount = Object.keys(variants).length;
  let totalDates = 0;
  if (rotation) {
    Object.values(rotation).forEach((dayData) => {
      (dayData.schedule || []).forEach((block) => { totalDates += (block.dates || []).length; });
    });
  }

  return (
    <div className="page">
      <header className="portal-header">
        <Link to={fixedClubId ? `/clubs/${fixedClubId}` : '/admin'} className="back-link">&larr; {fixedClubId ? 'Club' : 'Admin'}</Link>
        <h1>Racetimes</h1>
      </header>

      {/* Selectors */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        {!fixedClubId && (
          <label>Club:{' '}
            <select value={clubId} onChange={(e) => setClubId(e.target.value)} className="rt-select">
              {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        )}
        <label>Season:{' '}
          <select value={seasonId} onChange={(e) => setSeasonId(e.target.value)} className="rt-select">
            {seasons.map((s) => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}
          </select>
        </label>
      </div>

      {publishMsg && (
        <div className={publishMsg.type === 'error' ? 'error-banner' : 'rt-success-banner'} role="alert">
          {publishMsg.text}
        </div>
      )}

      {/* Change log */}
      {changeLog.length > 0 && (
        <div className="rt-changelog">
          <strong>Changes:</strong>
          <ul>{changeLog.map((c, i) => <li key={i}><span className="rt-time">{c.time}</span> {c.msg}</li>)}</ul>
        </div>
      )}

      {error && <p className="error">Error: {error}</p>}
      {loading && <p>Loading…</p>}

      {!loading && !error && (
        <>
          {/* Stats */}
          <div className="rt-stats">
            <span><strong>{variantCount}</strong> racetime variants</span>
            <span><strong>{rotation ? Object.keys(rotation).length : 0}</strong> rotation days</span>
            <span><strong>{totalDates}</strong> scheduled dates</span>
            <span><strong>{raceDates.length}</strong> date overrides</span>
          </div>

          {/* ① Season Summary — VIEW-ONLY matrix derived from the PUBLISHED
              schedule (race_schedule/*). The overview/navigation surface: it
              reflects the published result and is never hand-edited. Each date
              row drills (inline, one at a time) into the read-only per-day
              published view (ra-1kga). */}
          <PublishedMatrix
            publishedDates={matrixDates} isDraft={showDraftMatrix}
            availClasses={availClasses} courseCards={courseCards}
            loading={showDraftMatrix ? false : pubLoading} error={showDraftMatrix ? null : pubErr}
            selectedDate={selectedDate}
            onSelectDate={(d) => setSelectedDate(selectedDate === d ? null : d)}
          />

          {/* Race-day overrides — the ops surface, above the authoring one and
              outside Edit mode: on race day you are changing ONE date, live,
              not re-authoring the season (ra-53j9). */}
          <RaceDayOverrideSection
            publishedDates={publishedDates} raceDates={raceDates}
            loading={pubLoading}
            onApply={handleApplyRaceDayOverride} onClear={handleClearRaceDayOverride}
          />

          {/* ② Authoring source — the ONLY edit surface. Choice persists per
              event: Templated (variants + rotation, DRY) for DBSC/seasons, or
              Simple (direct per-date) for Cork Week/regattas (ra-1kga). */}
          <section className="rt-section">
            <div className="rt-section-head">
              <h3 style={{ marginBottom: 0 }}><span className="rt-step">2</span>Authoring source <span style={{ fontSize: '.72rem', fontWeight: 400, color: '#888' }}>— how this event is built before publishing</span></h3>
              {/* Edit toggle lives HERE, next to the authoring section it gates —
                  this is the only edit surface, so the control sits with it
                  rather than far up by the selectors (ra-c5r7). Toggling it also
                  flips the Season Summary above between published and draft. */}
              <button className={`rt-toggle-btn${editMode ? ' active' : ''}`} onClick={() => { setEditMode(!editMode); if (!editMode) setChangeLog([]); }}>
                {editMode ? 'Done' : 'Edit'}
              </button>
            </div>
            <div className="rt-auth-grid">
              <button type="button" className={`rt-auth-opt${effectiveMode === 'templated' ? ' sel' : ''}`} disabled={!editMode} onClick={() => handleChangeAuthoringMode('templated')}>
                <span className="rt-auth-radio">{effectiveMode === 'templated' ? '◉' : '○'}</span>
                <span className="rt-auth-tag">Templated</span>
                <h4>Variants + Rotation (DRY)</h4>
                <p>Define a few variants once, rotate them across the season. Best for <strong>whole seasons</strong> with repeating structure.</p>
              </button>
              {/* Simple is locked while templated content exists (ra-1kga): a DBSC-style
                  event with variants/rotation must keep its DRY builder, and switching to
                  direct per-date authoring would strand + risk clobbering that content on
                  the next Publish. Remove all variants + rotation to switch to Simple. */}
              <button type="button" className={`rt-auth-opt${effectiveMode === 'simple' ? ' sel' : ''}`} disabled={!editMode || hasTemplatedContent} title={hasTemplatedContent ? 'This event uses variants + rotation. Delete all variants and rotation blocks to author directly per-date.' : undefined} onClick={() => handleChangeAuthoringMode('simple')}>
                <span className="rt-auth-radio">{effectiveMode === 'simple' ? '◉' : '○'}</span>
                <span className="rt-auth-tag">Simple</span>
                <h4>Direct per-date</h4>
                <p>Type start times straight onto each date. Best for <strong>one-off regattas</strong>.</p>
              </button>
            </div>
            {!editMode && (
              <p style={{ fontSize: '.78rem', color: '#888', margin: '-.4rem 0 .8rem' }}>
                Press <strong>Edit</strong> to change the authoring source or edit content.
              </p>
            )}
            {editMode && hasTemplatedContent && (
              <p style={{ fontSize: '.78rem', color: '#888', margin: '-.4rem 0 .8rem' }}>
                This event uses the <strong>Templated</strong> builder (it has variants / rotation). Delete all variants and rotation blocks to switch to <strong>Simple</strong> direct per-date authoring.
              </p>
            )}

            {effectiveMode === 'templated' ? (
              // Gate the empty state on !hasTemplatedContent, NOT variantCount === 0:
              // an event can have a rotation but zero variants (the last variant was
              // deleted while rotation blocks remain). Keying on variantCount alone
              // would show "No variants yet" and hide RotationSection — but Simple is
              // locked because rotation still exists, stranding the user with no way to
              // delete those rotation blocks and unlock Simple (codex ra-1kga). Rendering
              // the full builder here keeps RotationSection's delete controls reachable.
              !hasTemplatedContent ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ fontSize: '1.1rem', marginBottom: '.5rem' }}>No racetime variants yet.</p>
                  {editMode && <button className="rt-btn-save" onClick={() => setShowAddForm(true)}>+ Add Variant</button>}
                </div>
              ) : (
                <>
                  <VariantSection
                    variants={variants} editMode={editMode} expanded={expanded}
                    setExpanded={setExpanded} availClasses={availClasses}
                    courseCards={courseCards}
                    onSaveMeta={handleSaveVariantMeta}
                    onSaveStarts={handleSaveStarts}
                    onDelete={handleDeleteVariant}
                    onShowAdd={() => setShowAddForm(true)}
                  />
                  <RotationSection
                    variants={variants} rotation={rotation} editMode={editMode}
                    onSave={handleSaveRotation}
                    logChange={logChange}
                  />
                  <RaceDateSection
                    raceDates={raceDates} editMode={editMode}
                    newRaceDate={newRaceDate} setNewRaceDate={setNewRaceDate}
                    onAdd={handleAddRaceDate} onDelete={handleDeleteRaceDate}
                    error={raceDateError}
                  />
                  {/* Course content (course cards) is published separately on the
                      Course Cards page; a newly-added card must be in a published
                      bundle before it's schedulable here (ra-yuuv). */}
                  <p className="rt-publish-hint" style={{ fontSize: '.76rem', color: '#888', margin: '.4rem 0 0' }}>
                    ℹ️ Course <strong>content</strong> (course cards) is published separately as a <strong>bundle</strong> on the <strong>Course Cards</strong> page.
                  </p>
                </>
              )
            ) : (
              <SimpleScheduleEditor
                dates={publishedDates} loading={pubLoading} error={pubErr}
                editMode={editMode} availClasses={availClasses} courseCards={courseCards}
                onSaveDate={handleSavePublishedDate} onDeleteDate={handleDeletePublishedDate}
                onAddDate={handleAddPublishedDate}
              />
            )}
          </section>

          {/* ③ Publish — both paths converge here. Templated GENERATES the
              enumerated race_schedule/* from variants + rotation; Simple edits
              are already written live per date (the Save Date button above). */}
          {editMode && effectiveMode === 'templated' && (
            <>
              <div className="rt-publish-bar">
                <span className="rt-publish-note"><span className="rt-step">3</span>Generates the enumerated <code>race_schedule/*</code> the app reads</span>
                <button className="rt-publish-btn" onClick={handlePublish} disabled={publishing}>
                  {publishing ? 'Publishing…' : 'Publish Schedule'}
                </button>
              </div>
              <div className="rt-publish-arrow">↓</div>
            </>
          )}

          {/* ③ Publish (Simple) — per-date Saves are already live; this explicit
              Publish re-stamps the published metadata so the Simple path has the
              same visible Publish step as Templated (ra-c5r7). */}
          {editMode && effectiveMode === 'simple' && (
            <>
              <div className="rt-publish-bar">
                <span className="rt-publish-note"><span className="rt-step">3</span>Each <strong>Save Date</strong> above is already live — <strong>Publish</strong> confirms the schedule and stamps the published metadata</span>
                <button className="rt-publish-btn" onClick={handlePublishSimple} disabled={publishing || !publishedDates.length}>
                  {publishing ? 'Publishing…' : 'Publish Schedule'}
                </button>
              </div>
              <div className="rt-publish-arrow">↓</div>
            </>
          )}

          {/* ③ Published Schedule — metadata + high-level stats only (per-day
              detail is via the matrix drill-down above, not enumerated here). */}
          <PublishedSummary
            publishedDates={publishedDates} index={publishedIndex}
            authoringMode={effectiveMode} availClasses={availClasses} loading={pubLoading}
          />

          {/* Add variant form — ref target for scroll-into-view on open (ra-yuuv) */}
          {showAddForm && (
            <div ref={addFormRef}>
              <AddVariantForm
                newVariant={newVariant} setNewVariant={setNewVariant}
                onCreate={handleCreateVariant} onCancel={() => { setShowAddForm(false); setVariantFormError(''); }}
                courseCards={courseCards}
                error={variantFormError}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Variant Section ──

function VariantSection({ variants, editMode, expanded, setExpanded, availClasses, courseCards, onSaveMeta, onSaveStarts, onDelete, onShowAdd }) {
  const byDay = {};
  Object.entries(variants).forEach(([vid, v]) => {
    const day = v.day || 'other';
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push({ id: vid, data: v });
  });

  return (
    <section className="rt-section">
      <h3>Racetime Variants</h3>
      <table className="rt-table">
        <thead>
          <tr>
            <th>ID</th><th>Day</th><th>Location</th><th>VHF</th><th>Fleet</th>
            <th>Course Card</th><th>Starts</th>
            {editMode && <th></th>}
          </tr>
        </thead>
        <tbody>
          {DAY_ORDER.map((day) => {
            const group = byDay[day];
            if (!group) return null;
            group.sort((a, b) => a.id.localeCompare(b.id));
            return group.map((item) => (
              <VariantRow key={item.id} vid={item.id} v={item.data}
                day={day} editMode={editMode}
                isExpanded={expanded === item.id}
                onToggle={() => setExpanded(expanded === item.id ? null : item.id)}
                availClasses={availClasses} courseCards={courseCards}
                onSaveMeta={onSaveMeta} onSaveStarts={onSaveStarts}
                onDelete={onDelete}
              />
            ));
          })}
        </tbody>
      </table>
      {editMode && (
        <div style={{ marginTop: '.75rem' }}>
          <button className="rt-btn-save" onClick={onShowAdd}>+ Add Variant</button>
        </div>
      )}
    </section>
  );
}

function VariantRow({ vid, v, day, editMode, isExpanded, onToggle, availClasses, courseCards, onSaveMeta, onSaveStarts, onDelete }) {
  const [meta, setMeta] = useState({});
  const [editedStarts, setEditedStarts] = useState([]);
  const [showPasteImport, setShowPasteImport] = useState(false);

  useEffect(() => {
    setMeta({
      name: v.name || '', location: v.location || '', vhf: v.vhf || '',
      fleet: v.fleet || '', course_card_id: v.course_card_id || '',
      document_set_id: v.document_set_id || '',
      sequence_minutes: v.sequence_minutes ?? store.DEFAULT_SEQUENCE_MINUTES,
    });
    setEditedStarts((v.starts || []).map((s) => ({
      time: s.time, flags: getFlags(s).join(', '), classes: getClasses(s).join(', '),
    })));
  }, [v]);

  const sequenceMinutes = Number(meta.sequence_minutes) || store.DEFAULT_SEQUENCE_MINUTES;
  const displaySequenceMinutes = v.sequence_minutes ?? store.DEFAULT_SEQUENCE_MINUTES;

  const colSpan = editMode ? 8 : 7;

  return (
    <>
      <tr className="rt-clickable" onClick={onToggle}>
        <td><code>{vid}</code></td>
        <td><span className={`rt-day-badge rt-day-${day}`}>{day}</span></td>
        <td><span className="rt-loc-badge">{v.location || '—'}</span></td>
        <td>{v.vhf ? `CH${v.vhf}` : '—'}</td>
        <td><span className={`rt-fleet-badge rt-fleet-${v.fleet || 'null'}`}>{v.fleet || 'combined'}</span></td>
        <td title={v.course_card_id || ''}>
          {cardNameById(courseCards, v.course_card_id) || (v.course_card_id ? <code style={{ fontSize: '.7rem' }}>{v.course_card_id}</code> : '—')}
          {/* The source has to survive the dropdown closing: a preview card sits
              in the table looking exactly like a live one otherwise (ra-29i6i). */}
          <CardSourceBadge note={cardSourceNoteById(courseCards, v.course_card_id)} />
        </td>
        <td>{(v.starts || []).length} classes</td>
        {editMode && (
          <td>
            <button className="rt-btn-sm rt-btn-del" onClick={(e) => { e.stopPropagation(); onDelete(vid); }} title="Delete variant">&times;</button>
          </td>
        )}
      </tr>
      {isExpanded && (
        <tr className="rt-starts-row open">
          <td colSpan={colSpan}>
            {editMode ? (
              <>
                {/* Meta editor */}
                <div className="rt-meta-form">
                  <label>Name:<input value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} /></label>
                  <label>Location:<input value={meta.location} onChange={(e) => setMeta({ ...meta, location: e.target.value })} style={{ width: '90px' }} /></label>
                  <label>VHF:<input value={meta.vhf} onChange={(e) => setMeta({ ...meta, vhf: e.target.value })} style={{ width: '40px' }} /></label>
                  <label>Fleet:<input value={meta.fleet} onChange={(e) => setMeta({ ...meta, fleet: e.target.value })} style={{ width: '60px' }} /></label>
                  <label>Course Card:<CourseCardSelect value={meta.course_card_id} onChange={(e) => setMeta({ ...meta, course_card_id: e.target.value })} courseCards={courseCards} style={{ width: '200px' }} /></label>
                  <label>Doc Set:<input value={meta.document_set_id} onChange={(e) => setMeta({ ...meta, document_set_id: e.target.value })} style={{ width: '140px' }} placeholder="dbsc-sat-hut" /></label>
                  <label title="Length of the start sequence (warning gun → start gun). DBSC: 3 min for Green/Thursday Red, 5 min otherwise.">
                    Sequence:
                    <select
                      value={String(sequenceMinutes)}
                      onChange={(e) => setMeta({ ...meta, sequence_minutes: Number(e.target.value) })}
                      style={{ width: '72px' }}
                    >
                      {store.SEQUENCE_MINUTES_OPTIONS.map((n) => (
                        <option key={n} value={n}>{n} min</option>
                      ))}
                    </select>
                  </label>
                  <button className="rt-btn-sm rt-btn-save" onClick={() => {
                    const data = {
                      name: meta.name,
                      location: meta.location || null,
                      vhf: meta.vhf || null,
                      fleet: meta.fleet || null,
                      course_card_id: meta.course_card_id || null,
                      document_set_id: meta.document_set_id || null,
                      sequence_minutes: sequenceMinutes,
                    };
                    onSaveMeta(vid, data);
                  }}>Save Meta</button>
                </div>
                {/* Starts editor. Warning = user-entered time (matches club paper doc).
                    Start = derived T=0 (what the app fires the countdown on). */}
                <p className="rt-sequence-hint" style={{ fontSize: '.72rem', color: '#666', margin: '.3rem 0' }}>
                  Enter <strong>warning signal</strong> times. Start gun = warning + {sequenceMinutes} min (sequence).
                </p>
                <table className="rt-starts-table">
                  <colgroup>
                    <col className="rt-col-start" />
                    <col className="rt-col-start" />
                    <col className="rt-col-flags" />
                    <col />
                    <col className="rt-col-act" />
                  </colgroup>
                  <thead><tr><th>Warning</th><th>Start</th><th>Flags</th><th>Classes</th><th></th></tr></thead>
                  <tbody>
                    {editedStarts.map((s, idx) => (
                      <tr key={idx}>
                        <td><input className="rt-time-input" value={s.time} onChange={(e) => {
                          const next = [...editedStarts];
                          next[idx] = { ...next[idx], time: e.target.value };
                          setEditedStarts(next);
                        }} /></td>
                        <td className="rt-derived-start" style={{ fontFamily: 'monospace', color: '#555' }}>
                          {s.time ? deriveStartTime(s.time, sequenceMinutes) : '—'}
                        </td>
                        <td><input value={s.flags} onChange={(e) => {
                          const next = [...editedStarts];
                          next[idx] = { ...next[idx], flags: e.target.value };
                          setEditedStarts(next);
                        }} placeholder="No. 1, Q" /></td>
                        <td><input value={s.classes} onChange={(e) => {
                          const next = [...editedStarts];
                          next[idx] = { ...next[idx], classes: e.target.value };
                          setEditedStarts(next);
                        }} placeholder="cruisers_0, b317" /></td>
                        <td><button className="rt-btn-sm rt-btn-del" onClick={() => {
                          setEditedStarts(editedStarts.filter((_, i) => i !== idx));
                        }}>&times;</button></td>
                      </tr>
                    ))}
                    <tr style={{ background: '#e8f5e9' }}>
                      <td colSpan={4} style={{ fontSize: '.75rem', color: '#888' }}>Use "Save All Starts" after editing</td>
                      <td><button className="rt-btn-sm rt-btn-add" onClick={() => {
                        setEditedStarts([...editedStarts, { time: '', flags: '', classes: '' }]);
                      }}>+</button></td>
                    </tr>
                  </tbody>
                </table>
                <div style={{ display: 'flex', gap: '.5rem', marginTop: '.4rem' }}>
                  <button className="rt-btn-sm rt-btn-save" onClick={() => {
                    const starts = editedStarts.filter((s) => s.time && s.classes).map((s) => ({
                      time: s.time.trim(),
                      flags: s.flags.split(',').map((f) => f.trim()).filter(Boolean),
                      classes: s.classes.split(',').map((c) => c.trim()).filter(Boolean),
                    }));
                    onSaveStarts(vid, starts);
                  }}>Save All Starts</button>
                  <button className="rt-btn-sm" onClick={() => setShowPasteImport(!showPasteImport)}>
                    {showPasteImport ? 'Hide Paste Import' : 'Paste Import'}
                  </button>
                </div>
                {showPasteImport && (
                  <StartsPasteImport
                    availClasses={availClasses}
                    existingClasses={editedStarts.flatMap((s) => s.classes.split(',').map((c) => c.trim()).filter(Boolean))}
                    onImport={(starts) => {
                      const newEdited = starts.map((s) => ({
                        time: s.time,
                        flags: s.flags.join(', '),
                        classes: s.classes.join(', '),
                      }));
                      setEditedStarts((prev) => [...prev, ...newEdited]);
                      setShowPasteImport(false);
                    }}
                    onClose={() => setShowPasteImport(false)}
                  />
                )}
              </>
            ) : (
              <>
                <strong style={{ fontSize: '.75rem' }}>{v.name}</strong>
                <span style={{ fontSize: '.7rem', color: '#666', marginLeft: '.5rem' }}>
                  Sequence: {displaySequenceMinutes} min
                </span>
                <table className="rt-starts-table">
                  <colgroup>
                    <col className="rt-col-start" />
                    <col className="rt-col-start" />
                    <col className="rt-col-flags" />
                    <col />
                  </colgroup>
                  <thead><tr><th>Warning</th><th>Start</th><th>Flags</th><th>Classes</th></tr></thead>
                  <tbody>
                    {(v.starts || []).map((s, idx) => (
                      <tr key={idx}>
                        <td>{s.time}</td>
                        <td style={{ fontFamily: 'monospace' }}>
                          {s.time ? deriveStartTime(s.time, displaySequenceMinutes) : '—'}
                        </td>
                        <td>{getFlags(s).join(', ')}</td>
                        <td>{getClasses(s).map((c) => className(c, availClasses)).join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Rotation Section ──

function RotationSection({ variants, rotation, editMode, onSave, logChange }) {
  if (!rotation && !editMode) return null;
  const rot = rotation || {};

  async function saveBlock(day, blockIdx, dates, blockVariants) {
    const newRot = { ...rot };
    newRot[day] = { ...newRot[day], schedule: [...(newRot[day]?.schedule || [])] };
    newRot[day].schedule[blockIdx] = { variants: blockVariants, dates };
    await onSave(newRot);
    logChange(`Updated rotation block ${blockIdx + 1} for ${day}`);
  }

  async function deleteBlock(day, blockIdx) {
    if (!confirm('Delete this rotation block?')) return;
    const newRot = { ...rot };
    newRot[day] = { ...newRot[day], schedule: [...(newRot[day]?.schedule || [])] };
    newRot[day].schedule.splice(blockIdx, 1);
    if (newRot[day].schedule.length === 0) delete newRot[day];
    await onSave(newRot);
    logChange(`Deleted rotation block from ${day}`);
  }

  async function addBlock(day, dates, blockVariants) {
    const newRot = { ...rot };
    if (!newRot[day]) newRot[day] = { schedule: [] };
    else newRot[day] = { ...newRot[day], schedule: [...(newRot[day].schedule || [])] };
    newRot[day].schedule.push({ variants: blockVariants, dates });
    await onSave(newRot);
    logChange(`Added rotation block to ${day} (${dates.length} dates)`);
  }

  async function addDay(day) {
    const newRot = { ...rot, [day]: { schedule: [] } };
    await onSave(newRot);
    logChange(`Added rotation day: ${day}`);
  }

  async function deleteDay(day) {
    if (!confirm(`Delete all rotation data for ${day}?`)) return;
    const newRot = { ...rot };
    delete newRot[day];
    await onSave(newRot);
    logChange(`Deleted rotation day: ${day}`);
  }

  const usedDays = Object.keys(rot);
  const availDays = DAY_ORDER.filter((d) => !usedDays.includes(d));

  return (
    <section className="rt-section">
      <h3>Rotation Schedule</h3>
      {!rotation && editMode && <p style={{ fontSize: '.85rem', color: '#888' }}>No rotation schedule yet.</p>}
      {DAY_ORDER.map((day) => {
        const dayData = rot[day];
        if (!dayData && !editMode) return null;
        return (
          <div key={day}>
            <h4 style={{ margin: '.75rem 0 .35rem' }}>
              <span className={`rt-day-badge rt-day-${day}`}>{day.charAt(0).toUpperCase() + day.slice(1)}</span>
              {editMode && dayData && (
                <button className="rt-btn-sm rt-btn-del" onClick={() => deleteDay(day)} style={{ verticalAlign: 'middle', marginLeft: '.5rem' }}>&times;</button>
              )}
            </h4>
            {dayData && (dayData.schedule || []).map((block, blockIdx) => (
              <RotationBlock key={blockIdx} block={block} day={day} blockIdx={blockIdx}
                editMode={editMode} variants={variants}
                onSave={(dates, vars) => saveBlock(day, blockIdx, dates, vars)}
                onDelete={() => deleteBlock(day, blockIdx)}
              />
            ))}
            {editMode && <AddRotationBlock day={day} variants={variants} onAdd={(dates, vars) => addBlock(day, dates, vars)} />}
          </div>
        );
      })}
      {editMode && availDays.length > 0 && <AddDayControl availDays={availDays} onAdd={addDay} />}
    </section>
  );
}

function RotationBlock({ block, day, blockIdx, editMode, variants, onSave, onDelete }) {
  const [dates, setDates] = useState((block.dates || []).join(', '));
  const [blockVars, setBlockVars] = useState((block.variants || []).join(', '));

  if (!editMode) {
    return (
      <div className="rt-sched-block">
        <div className="rt-sched-dates">
          {(block.dates || []).map(formatShortDate).join(', ')}
          <span style={{ color: '#ccc' }}> ({(block.dates || []).length})</span>
        </div>
        <div className="rt-sched-variants">
          {(block.variants || []).map((vid) => {
            const v = variants[vid];
            return (
              <span key={vid} className="rt-sched-chip">
                {v && <span className={`rt-fleet-badge rt-fleet-${v.fleet || 'null'}`} style={{ fontSize: '.6rem' }}>{v.fleet || 'all'}</span>}
                <span className="rt-loc-badge" style={{ fontSize: '.6rem' }}>{v ? v.location : vid}</span>
                {v?.vhf && <span style={{ fontSize: '.65rem', color: '#888' }}>CH{v.vhf}</span>}
              </span>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="rt-sched-block">
      <div style={{ marginBottom: '.4rem' }}>
        <label style={{ fontSize: '.7rem', fontWeight: 600, color: '#888' }}>Dates (YYYY-MM-DD, comma-separated):</label>
        <textarea className="rt-edit-textarea" value={dates} onChange={(e) => setDates(e.target.value)} rows={2} />
      </div>
      <div style={{ marginBottom: '.4rem' }}>
        <label style={{ fontSize: '.7rem', fontWeight: 600, color: '#888' }}>Variants (comma-separated IDs):</label>
        <input className="rt-edit-input" value={blockVars} onChange={(e) => setBlockVars(e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: '.4rem' }}>
        <button className="rt-btn-sm rt-btn-save" onClick={() => {
          const parsedDates = dates.split(/[,\n]+/).map((d) => d.trim()).filter(Boolean);
          const parsedVars = blockVars.split(',').map((v) => v.trim()).filter(Boolean);
          onSave(parsedDates, parsedVars);
        }}>Save Block</button>
        <button className="rt-btn-sm rt-btn-del" onClick={onDelete}>&times; Delete Block</button>
      </div>
    </div>
  );
}

function AddRotationBlock({ day, variants, onAdd }) {
  const [dates, setDates] = useState('');
  const [vars, setVars] = useState('');
  const [blockError, setBlockError] = useState('');
  return (
    <div className="rt-sched-block" style={{ background: '#e8f5e9', borderStyle: 'dashed' }}>
      {blockError && <p className="field-error" role="alert">{blockError}</p>}
      <div style={{ marginBottom: '.4rem' }}>
        <label style={{ fontSize: '.7rem', fontWeight: 600, color: '#888' }}>New block dates:</label>
        <input className="rt-edit-input" value={dates} onChange={(e) => setDates(e.target.value)} placeholder="2025-06-07, 2025-06-14, ..." />
      </div>
      <div style={{ marginBottom: '.4rem' }}>
        <label style={{ fontSize: '.7rem', fontWeight: 600, color: '#888' }}>Variants:</label>
        <input className="rt-edit-input" value={vars} onChange={(e) => setVars(e.target.value)} placeholder="sat_hut_blue, sat_corinthian_red" />
      </div>
      <button className="rt-btn-sm rt-btn-save" onClick={() => {
        const parsedDates = dates.split(/[,\n]+/).map((d) => d.trim()).filter(Boolean);
        const parsedVars = vars.split(',').map((v) => v.trim()).filter(Boolean);
        if (!parsedDates.length || !parsedVars.length) { setBlockError('Dates and at least one variant are required'); return; }
        setBlockError('');
        onAdd(parsedDates, parsedVars);
        setDates('');
        setVars('');
      }}>+ Add Block</button>
    </div>
  );
}

function AddDayControl({ availDays, onAdd }) {
  const [day, setDay] = useState(availDays[0]);
  return (
    <div style={{ marginTop: '.75rem', display: 'flex', alignItems: 'center', gap: '.4rem' }}>
      <select className="rt-select" value={day} onChange={(e) => setDay(e.target.value)}>
        {availDays.map((d) => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
      </select>
      <button className="rt-btn-sm rt-btn-save" onClick={() => onAdd(day)}>+ Add Day</button>
    </div>
  );
}

// ── Race Date Section ──

function RaceDateSection({ raceDates, editMode, newRaceDate, setNewRaceDate, onAdd, onDelete, error }) {
  const sorted = [...raceDates].sort((a, b) => (a._id || '').localeCompare(b._id || ''));
  return (
    <section className="rt-section">
      <h3>Race Date Overrides</h3>
      {sorted.length === 0 && !editMode && (
        <p style={{ fontSize: '.85rem', color: '#888' }}>No overrides — all dates use rotation defaults.</p>
      )}
      {sorted.length > 0 && (
        <table className="rt-table">
          <thead>
            <tr>
              <th>Date</th><th>Status</th><th>Delay</th><th>Override Variants</th><th>Notes</th>
              {editMode && <th></th>}
            </tr>
          </thead>
          <tbody>
            {sorted.map((rd) => (
              <tr key={rd._id}>
                <td><code>{rd._id}</code></td>
                <td>{rd.status || '—'}</td>
                <td>{rd.delay_minutes ? `+${rd.delay_minutes}min` : '—'}</td>
                <td style={{ fontSize: '.75rem' }}>{(rd.override_variants || []).join(', ') || '—'}</td>
                <td style={{ fontSize: '.75rem' }}>{rd.notes || ''}</td>
                {editMode && (
                  <td><button className="rt-btn-sm rt-btn-del" onClick={() => onDelete(rd._id)}>&times;</button></td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editMode && (
        <div className="rt-override-form">
          <strong>Add Override:</strong>
          {error && <p className="field-error" role="alert">{error}</p>}
          <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '.4rem' }}>
            <label>Date:<input type="date" value={newRaceDate.date} onChange={(e) => setNewRaceDate({ ...newRaceDate, date: e.target.value })} className="rt-edit-input" style={{ width: '140px' }} /></label>
            <label>Status:
              <select value={newRaceDate.status} onChange={(e) => setNewRaceDate({ ...newRaceDate, status: e.target.value })} className="rt-edit-input">
                <option value="on_schedule">on_schedule</option>
                <option value="delayed">delayed</option>
                <option value="abandoned">abandoned</option>
                <option value="cancelled">cancelled</option>
              </select>
            </label>
            <label>Delay (min):<input type="number" value={newRaceDate.delay} onChange={(e) => setNewRaceDate({ ...newRaceDate, delay: e.target.value })} className="rt-edit-input" style={{ width: '55px' }} /></label>
            <label>Override variants:<input value={newRaceDate.variants} onChange={(e) => setNewRaceDate({ ...newRaceDate, variants: e.target.value })} className="rt-edit-input" placeholder="sat_combined, ..." style={{ width: '200px' }} /></label>
            <label>Notes:<input value={newRaceDate.notes} onChange={(e) => setNewRaceDate({ ...newRaceDate, notes: e.target.value })} className="rt-edit-input" placeholder="Reason" style={{ flex: 1, minWidth: '150px' }} /></label>
            <button className="rt-btn-sm rt-btn-save" onClick={onAdd}>+ Add</button>
          </div>
        </div>
      )}
    </section>
  );
}

// ── ① Season Summary: VIEW-ONLY matrix from the PUBLISHED schedule ──
//
// Built from race_schedule/* (the live docs the app reads), NOT from variants —
// so it reflects the published result and works for BOTH authoring paths. It is
// never hand-edited. Each date row drills, inline and one at a time, into the
// read-only per-day published view (ra-1kga).

function PublishedMatrix({ publishedDates, isDraft, availClasses, courseCards, loading, error, selectedDate, onSelectDate }) {
  // Class column order: first-seen across all published starts.
  const classOrder = [];
  const classSeen = {};
  publishedDates.forEach((d) => {
    Object.values(d.locations || {}).forEach((loc) => {
      (loc.starts || []).forEach((s) => {
        getClasses(s).forEach((cid) => {
          if (!classSeen[cid]) { classSeen[cid] = true; classOrder.push(cid); }
        });
      });
    });
  });

  // matrix[date][classId] = [{ loc, locId, time, fleet }]; >1 entry = conflict.
  const matrix = {};
  const sortedDates = [...publishedDates].sort((a, b) => a.date.localeCompare(b.date));
  let conflicts = 0;
  const conflictList = [];
  sortedDates.forEach((d) => {
    matrix[d.date] = {};
    Object.entries(d.locations || {}).forEach(([locId, loc]) => {
      (loc.starts || []).forEach((s) => {
        getClasses(s).forEach((cid) => {
          if (!matrix[d.date][cid]) matrix[d.date][cid] = [];
          matrix[d.date][cid].push({ loc: loc.name || locId, locId, time: s.time, fleet: loc.fleet });
        });
      });
    });
    classOrder.forEach((cid) => {
      const hits = matrix[d.date][cid] || [];
      if (hits.length > 1) {
        conflicts++;
        conflictList.push({ date: d.date, className: className(cid, availClasses), locations: hits.map((h) => locAbbrev(h.locId)) });
      }
    });
  });

  return (
    <section className="rt-section">
      <h3>
        <span className="rt-step">1</span>Season Summary — Validation Matrix{' '}
        {isDraft ? (
          <span className="rt-draft-pill" title="Showing your unpublished changes — Publish to make them live">DRAFT — unpublished changes</span>
        ) : (
          <span style={{ fontSize: '.72rem', fontWeight: 400, color: '#888' }}>(overview &amp; navigation — view only)</span>
        )}
      </h3>
      {loading ? (
        <p>Loading published schedule…</p>
      ) : error ? (
        <p className="error">Error: {error}</p>
      ) : !sortedDates.length ? (
        <p style={{ fontSize: '.85rem', color: '#888' }}>
          {isDraft
            ? 'No draft schedule yet — add variants + rotation below, then Publish to make it live.'
            : 'No published schedule yet. Author below and Publish to populate this overview.'}
        </p>
      ) : !classOrder.length ? (
        <p style={{ fontSize: '.85rem', color: '#888' }}>{sortedDates.length} published date{sortedDates.length > 1 ? 's' : ''}, but no class starts to summarise yet.</p>
      ) : (
        <>
          <p style={{ fontSize: '.8rem', marginBottom: '.4rem' }}>
            {sortedDates.length} dates &times; {classOrder.length} classes
            {conflicts > 0 && <> — <span style={{ color: '#c62828', fontWeight: 700 }}>&#9888; {conflicts} conflict{conflicts > 1 ? 's' : ''}</span></>}
          </p>
          <p style={{ fontSize: '.76rem', color: isDraft ? '#b26a00' : '#1a73e8', margin: '0 0 .5rem' }}>
            {isDraft ? (
              <>&#9998; Showing your <strong>unpublished draft</strong> (what Publish would generate). Click any date row to preview that day.</>
            ) : (
              <>&#128073; Click any date row to drill into its <strong>read-only published schedule</strong>.</>
            )}
          </p>
          {conflictList.length > 0 && (
            <div style={{ background: '#ffebee', border: '2px solid #ef9a9a', borderRadius: '4px', padding: '.5rem .75rem', marginBottom: '.6rem', fontSize: '.78rem' }}>
              <strong style={{ color: '#c62828' }}>&#9888; Class published in two places on the same day:</strong>
              <ul style={{ margin: '.3rem 0 0', paddingLeft: '1.2rem' }}>
                {conflictList.map((c, i) => (
                  <li key={i}><strong>{formatShortDate(c.date)}</strong> — <strong>{c.className}</strong> in {c.locations.join(' + ')}</li>
                ))}
              </ul>
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table className="rt-val-table">
              <thead>
                <tr>
                  <th className="rt-val-date">Date</th>
                  {classOrder.map((cid) => (
                    <th key={cid} title={className(cid, availClasses)}>{cid.replace('cruisers_', 'Cr').replace('flying_fifteen', 'FF')}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedDates.map((d) => {
                  const isSel = selectedDate === d.date;
                  const dow = new Date(d.date + 'T12:00:00').toLocaleDateString('en', { weekday: 'short' });
                  return (
                    <Fragment key={d.date}>
                      <tr className={`rt-val-row${isSel ? ' rt-val-row-sel' : ''}`} onClick={() => onSelectDate(d.date)}>
                        <td className="rt-val-date">
                          {formatShortDate(d.date)} <span style={{ color: '#ccc' }}>{dow}</span>
                          <span className="rt-val-caret">{isSel ? '▾' : '▸'}</span>
                        </td>
                        {classOrder.map((cid) => {
                          const hits = matrix[d.date][cid] || [];
                          if (hits.length === 0) return <td key={cid} className="rt-val-cell rt-val-empty">&middot;</td>;
                          if (hits.length === 1) {
                            const h = hits[0];
                            return (
                              <td key={cid} className={`rt-val-cell rt-val-${h.fleet || 'null'}`} title={h.loc}>
                                <span className="rt-val-loc">{locAbbrev(h.locId)}</span><br />{h.time}
                              </td>
                            );
                          }
                          return (
                            <td key={cid} className="rt-val-cell rt-val-conflict" title={hits.map((h) => h.loc).join(', ')}>
                              &#9888; {hits.map((h) => locAbbrev(h.locId)).join('+')}<br />{hits.map((h) => h.time).join('/')}
                            </td>
                          );
                        })}
                      </tr>
                      {isSel && (
                        <tr>
                          <td colSpan={classOrder.length + 1} style={{ padding: '.2rem .2rem .6rem', border: 'none' }}>
                            <div className="rt-drill">
                              <div className="rt-drill-head">{formatShortDate(d.date)} — {isDraft ? 'Draft' : 'Published'} schedule for this day <span className="rt-ro-pill">{isDraft ? 'DRAFT' : 'READ ONLY'}</span></div>
                              <PublishedDateEditor
                                dateDoc={d} editMode={false}
                                availClasses={availClasses} courseCards={courseCards}
                                onSave={noop} onDelete={noop}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

// ── Published Schedule Section (race_schedule/*) ──
//
// Reads + edits the dated docs the APP actually consumes. Critically this also
// surfaces schedules authored DIRECTLY into race_schedule/{date} (no variants) —
// previously invisible/uneditable in this UI (ra-gbrd.25). Edits go live with no
// separate Publish step (these ARE the published docs).

function dayNameForDate(iso) {
  // Local-noon avoids the date rolling back a day under negative UTC offsets.
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return d.toLocaleDateString('en', { weekday: 'long' }).toLowerCase();
}

// ── ② Simple authoring: direct per-date editing of race_schedule/* ──
//
// The "Simple" path (Cork Week / one-off regattas). Edits write the live
// race_schedule/{date} docs directly — there is no separate Publish step; each
// "Save Date" goes live. Props-driven (the page owns the loaded data + handlers
// so the same source feeds the matrix and summary).

function SimpleScheduleEditor({ dates, loading, error, editMode, availClasses, courseCards, onSaveDate, onDeleteDate, onAddDate }) {
  const [showAddDate, setShowAddDate] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [addErr, setAddErr] = useState('');

  async function handleAdd() {
    const d = newDate.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) { setAddErr('Enter a date as YYYY-MM-DD.'); return; }
    if (dates.some((x) => x.date === d)) { setAddErr(`${d} already has a schedule.`); return; }
    setAddErr('');
    await onAddDate(d);
    setNewDate('');
    setShowAddDate(false);
  }

  return (
    <div className="rt-pub-section" style={{ marginTop: '.5rem' }}>
      <p style={{ fontSize: '.78rem', color: '#666', margin: '0 0 .6rem' }}>
        Author race times directly onto each date. Each <strong>Save Date</strong> goes <strong>live immediately</strong>; <strong>Publish</strong> below confirms the schedule and stamps the published metadata.
      </p>
      {error && <p className="error">Error: {error}</p>}
      {loading ? (
        <p>Loading published schedule…</p>
      ) : dates.length === 0 ? (
        <p style={{ fontSize: '.85rem', color: '#888' }}>
          No dates yet.{editMode ? ' Use “+ Add Date” below to author one.' : ''}
        </p>
      ) : (
        dates.map((d) => (
          <PublishedDateEditor
            key={d.date} dateDoc={d} editMode={editMode}
            availClasses={availClasses} courseCards={courseCards}
            onSave={onSaveDate} onDelete={onDeleteDate}
          />
        ))
      )}
      {editMode && (
        <div style={{ marginTop: '.75rem' }}>
          {showAddDate ? (
            <div className="rt-add-form">
              <h4 style={{ margin: '0 0 .5rem' }}>Add Date</h4>
              {addErr && <p className="field-error" role="alert">{addErr}</p>}
              <div style={{ display: 'flex', gap: '.5rem', alignItems: 'end', flexWrap: 'wrap' }}>
                <label>Date:<br /><input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="rt-edit-input" style={{ width: '160px' }} /></label>
                <button className="rt-btn-save" onClick={handleAdd}>Create</button>
                <button className="rt-btn-cancel" onClick={() => { setShowAddDate(false); setAddErr(''); setNewDate(''); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button className="rt-btn-save" onClick={() => setShowAddDate(true)}>+ Add Date</button>
          )}
        </div>
      )}
    </div>
  );
}

// ── ③ Published Schedule summary — metadata + high-level stats ──
//
// Replaces the full per-date enumeration (which for DBSC was ~48 A4 pages and
// too comprehensive) with the published METADATA and high-level stats. Per-day
// detail lives in the matrix drill-down above, not here (ra-1kga).

function PublishedSummary({ publishedDates, index, authoringMode, loading }) {
  let locationCount = 0;
  let startCount = 0;
  let conflicts = 0;
  const classSet = new Set();
  publishedDates.forEach((d) => {
    const byClass = {};
    Object.values(d.locations || {}).forEach((loc) => {
      locationCount++;
      (loc.starts || []).forEach((s) => {
        startCount++;
        getClasses(s).forEach((cid) => { classSet.add(cid); byClass[cid] = (byClass[cid] || 0) + 1; });
      });
    });
    Object.values(byClass).forEach((n) => { if (n > 1) conflicts++; });
  });

  // _index.published_at may be a Firestore Timestamp or an ISO string.
  let whenText = null;
  const publishedAt = index?.published_at;
  if (publishedAt) {
    try {
      const dt = typeof publishedAt.toDate === 'function' ? publishedAt.toDate() : new Date(publishedAt);
      if (!Number.isNaN(dt.getTime())) whenText = dt.toLocaleString();
    } catch { /* ignore unparseable timestamp */ }
  }

  return (
    <section className="rt-section rt-pub-section">
      <h3><span className="rt-step">3</span>Published Schedule <span style={{ fontSize: '.72rem', fontWeight: 400, color: '#c62828' }}>&#9679; live — what the app reads</span></h3>
      {loading ? (
        <p>Loading…</p>
      ) : publishedDates.length === 0 ? (
        <p style={{ fontSize: '.85rem', color: '#888' }}>Nothing published yet.</p>
      ) : (
        <>
          <div className="rt-summary-grid">
            <span className="rt-summary-stat"><strong>{publishedDates.length}</strong> dates</span>
            <span className="rt-summary-stat"><strong>{locationCount}</strong> start locations</span>
            <span className="rt-summary-stat"><strong>{classSet.size}</strong> classes</span>
            <span className="rt-summary-stat"><strong>{startCount}</strong> starts</span>
            {conflicts > 0 && <span className="rt-summary-stat" style={{ color: '#c62828' }}><strong>&#9888; {conflicts}</strong> conflict{conflicts > 1 ? 's' : ''}</span>}
          </div>
          <p className="rt-summary-meta">
            Source: <span className="rt-approach-badge">{authoringMode === 'simple' ? 'Simple (direct)' : 'Templated (variants + rotation)'}</span>
            {whenText && <> · last published {whenText}</>}
            {index?.published_by && <> by {index.published_by}</>}
          </p>
          <p style={{ fontSize: '.76rem', color: '#888', margin: '.3rem 0 0' }}>
            Per-day detail is in the <strong>Season Summary</strong> above — click a date row to view its published schedule.
          </p>
        </>
      )}
    </section>
  );
}

// Seed an editable buffer from a published date doc. Starts carry the original
// `time` and any `sequence_minutes` so buildPublishedStart can keep warning_time
// consistent if the start time is edited.
function bufferFromDateDoc(dateDoc) {
  const locations = {};
  Object.entries(dateDoc.locations || {}).forEach(([locId, loc]) => {
    locations[locId] = {
      name: loc.name || '',
      vhf: loc.vhf || '',
      fleet: loc.fleet || '',
      course_card_id: loc.course_card_id || '',
      document_set_id: loc.document_set_id || '',
      sequence_minutes: loc.sequence_minutes ?? null,
      // Not editable here (no UI yet) but carried so a save doesn't erase it —
      // this buffer IS the doc that gets written back (ra-8en2.1.4). Same for
      // the race-day committee vessel (ra-53j9): it is authored in the
      // override section, and an ordinary start-time edit saved from here
      // would otherwise put the fleet back on the wrong boat, live.
      active_window: loc.active_window || null,
      committee_vessel: loc.committee_vessel || null,
      starts: (loc.starts || []).map((s) => ({
        time: s.time || '',
        flags: getFlags(s).join(', '),
        classes: getClasses(s).join(', '),
        sequence_minutes: s.sequence_minutes ?? null,
      })),
    };
  });
  return { day: dateDoc.day || '', locations, active_window: dateDoc.active_window || null };
}

function PublishedDateEditor({ dateDoc, editMode, availClasses, courseCards, onSave, onDelete }) {
  const [buf, setBuf] = useState(() => bufferFromDateDoc(dateDoc));
  const [saving, setSaving] = useState(false);
  const [newLoc, setNewLoc] = useState({ id: '', name: '' });

  useEffect(() => { setBuf(bufferFromDateDoc(dateDoc)); }, [dateDoc]);

  const locEntries = Object.entries(buf.locations);

  function updateLoc(locId, patch) {
    setBuf((prev) => ({
      ...prev,
      locations: { ...prev.locations, [locId]: { ...prev.locations[locId], ...patch } },
    }));
  }
  function updateStart(locId, idx, patch) {
    setBuf((prev) => {
      const starts = [...prev.locations[locId].starts];
      starts[idx] = { ...starts[idx], ...patch };
      return { ...prev, locations: { ...prev.locations, [locId]: { ...prev.locations[locId], starts } } };
    });
  }
  function addStart(locId) {
    setBuf((prev) => {
      const starts = [...prev.locations[locId].starts, { time: '', flags: '', classes: '', sequence_minutes: null }];
      return { ...prev, locations: { ...prev.locations, [locId]: { ...prev.locations[locId], starts } } };
    });
  }
  function removeStart(locId, idx) {
    setBuf((prev) => {
      const starts = prev.locations[locId].starts.filter((_, i) => i !== idx);
      return { ...prev, locations: { ...prev.locations, [locId]: { ...prev.locations[locId], starts } } };
    });
  }
  function removeLoc(locId) {
    if (!confirm(`Remove location "${locId}" from ${dateDoc.date}?`)) return;
    setBuf((prev) => {
      const locations = { ...prev.locations };
      delete locations[locId];
      return { ...prev, locations };
    });
  }
  function addLoc() {
    const id = newLoc.id.trim();
    if (!id || buf.locations[id]) return;
    setBuf((prev) => ({
      ...prev,
      locations: {
        ...prev.locations,
        [id]: { name: newLoc.name.trim() || id, vhf: '', fleet: '', course_card_id: '', document_set_id: '', sequence_minutes: null, starts: [] },
      },
    }));
    setNewLoc({ id: '', name: '' });
  }

  async function save() {
    setSaving(true);
    try {
      const locations = {};
      Object.entries(buf.locations).forEach(([locId, loc]) => {
        locations[locId] = {
          name: loc.name || locId,
          vhf: loc.vhf || null,
          course_card_id: loc.course_card_id || null,
          document_set_id: loc.document_set_id || null,
          fleet: loc.fleet || null,
          ...(loc.sequence_minutes != null ? { sequence_minutes: store.getSequenceMinutes({ sequence_minutes: loc.sequence_minutes }) } : {}),
          ...(loc.active_window ? { active_window: loc.active_window } : {}),
          ...(loc.committee_vessel ? { committee_vessel: loc.committee_vessel } : {}),
          starts: loc.starts
            .filter((s) => s.time && s.classes)
            .map((s) => store.buildPublishedStart({
              time: s.time,
              flags: s.flags.split(',').map((f) => f.trim()).filter(Boolean),
              classes: s.classes.split(',').map((c) => c.trim()).filter(Boolean),
              sequence_minutes: s.sequence_minutes,
            }))
            .sort((a, b) => a.time.localeCompare(b.time)),
        };
      });
      await onSave(dateDoc.date, {
        day: buf.day || dayNameForDate(dateDoc.date),
        locations,
        active_window: buf.active_window,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rt-pub-date">
      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap', margin: '.4rem 0 .35rem' }}>
        <strong>{formatShortDate(dateDoc.date)}</strong>
        <span style={{ fontFamily: 'monospace', fontSize: '.72rem', color: '#999' }}>{dateDoc.date}</span>
        <span className={`rt-day-badge rt-day-${buf.day || 'other'}`}>{buf.day || '—'}</span>
        {editMode && (
          <button className="rt-btn-sm rt-btn-del" onClick={() => onDelete(dateDoc.date)} title="Delete this published date">&times; Date</button>
        )}
      </div>

      {locEntries.length === 0 && (
        <p style={{ fontSize: '.78rem', color: '#888', margin: '.2rem 0' }}>No start locations on this date.</p>
      )}

      {locEntries.map(([locId, loc]) => (
        <div key={locId} className="rt-pub-loc">
          <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', flexWrap: 'wrap' }}>
            <span className="rt-loc-badge">{locId}</span>
            {editMode ? (
              <input value={loc.name} onChange={(e) => updateLoc(locId, { name: e.target.value })} placeholder="Location name" style={{ width: '150px' }} />
            ) : (
              <strong style={{ fontSize: '.8rem' }}>{loc.name || locId}</strong>
            )}
            {editMode ? (
              <>
                <label style={{ fontSize: '.7rem' }}>VHF<input value={loc.vhf} onChange={(e) => updateLoc(locId, { vhf: e.target.value })} style={{ width: '40px' }} /></label>
                <label style={{ fontSize: '.7rem' }}>Fleet<input value={loc.fleet} onChange={(e) => updateLoc(locId, { fleet: e.target.value })} style={{ width: '60px' }} /></label>
                <label style={{ fontSize: '.7rem' }}>Card<CourseCardSelect value={loc.course_card_id} onChange={(e) => updateLoc(locId, { course_card_id: e.target.value })} courseCards={courseCards} style={{ width: '160px' }} /></label>
                <label style={{ fontSize: '.7rem' }}>Doc<input value={loc.document_set_id} onChange={(e) => updateLoc(locId, { document_set_id: e.target.value })} style={{ width: '120px' }} placeholder="doc-set-id" /></label>
                <button className="rt-btn-sm rt-btn-del" onClick={() => removeLoc(locId)} title="Remove location">&times;</button>
              </>
            ) : (
              <>
                {loc.vhf && <span style={{ fontSize: '.7rem', color: '#888' }}>CH{loc.vhf}</span>}
                {loc.fleet && <span className={`rt-fleet-badge rt-fleet-${loc.fleet || 'null'}`} style={{ fontSize: '.6rem' }}>{loc.fleet}</span>}
                {loc.course_card_id && (
                  <span style={{ fontSize: '.7rem', color: '#666' }}>
                    {cardNameById(courseCards, loc.course_card_id) || loc.course_card_id}
                    <CardSourceBadge note={cardSourceNoteById(courseCards, loc.course_card_id)} />
                  </span>
                )}
              </>
            )}
          </div>

          <table className="rt-starts-table">
            <colgroup>
              <col className="rt-col-start" />
              <col className="rt-col-flags" />
              <col />
              {editMode && <col className="rt-col-act" />}
            </colgroup>
            <thead><tr><th>Start</th><th>Flags</th><th>Classes</th>{editMode && <th></th>}</tr></thead>
            <tbody>
              {loc.starts.map((s, idx) => (
                editMode ? (
                  <tr key={idx}>
                    <td><input className="rt-time-input" value={s.time} onChange={(e) => updateStart(locId, idx, { time: e.target.value })} placeholder="HH:MM" /></td>
                    <td><input value={s.flags} onChange={(e) => updateStart(locId, idx, { flags: e.target.value })} placeholder="No. 1" /></td>
                    <td><input value={s.classes} onChange={(e) => updateStart(locId, idx, { classes: e.target.value })} placeholder="cruisers_0, b317" /></td>
                    <td><button className="rt-btn-sm rt-btn-del" onClick={() => removeStart(locId, idx)}>&times;</button></td>
                  </tr>
                ) : (
                  <tr key={idx}>
                    <td style={{ fontFamily: 'monospace' }}>{s.time || '—'}</td>
                    <td>{s.flags}</td>
                    <td>{s.classes.split(',').map((c) => c.trim()).filter(Boolean).map((c) => className(c, availClasses)).join(', ')}</td>
                  </tr>
                )
              ))}
              {editMode && (
                <tr style={{ background: '#e8f5e9' }}>
                  <td colSpan={3} style={{ fontSize: '.72rem', color: '#888' }}>Times are START guns (T=0) — what the app reads</td>
                  <td><button className="rt-btn-sm rt-btn-add" onClick={() => addStart(locId)}>+</button></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ))}

      {editMode && (
        <>
          <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center', flexWrap: 'wrap', margin: '.4rem 0' }}>
            <input value={newLoc.id} onChange={(e) => setNewLoc({ ...newLoc, id: e.target.value })} placeholder="new location id (e.g. hut)" style={{ width: '160px' }} />
            <input value={newLoc.name} onChange={(e) => setNewLoc({ ...newLoc, name: e.target.value })} placeholder="name (optional)" style={{ width: '150px' }} />
            <button className="rt-btn-sm rt-btn-add" onClick={addLoc} disabled={!newLoc.id.trim() || !!buf.locations[newLoc.id.trim()]}>+ Add Location</button>
          </div>
          <button className="rt-btn-sm rt-btn-save" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Date'}</button>
        </>
      )}
    </div>
  );
}

// ── Add Variant Form ──

function AddVariantForm({ newVariant, setNewVariant, onCreate, onCancel, courseCards, error }) {
  const upd = (field) => (e) => setNewVariant({ ...newVariant, [field]: e.target.value });
  return (
    <div className="rt-add-form">
      <h4 style={{ margin: '0 0 .5rem' }}>Add Racetime Variant</h4>
      {error && <p className="field-error" role="alert">{error}</p>}
      <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap', alignItems: 'end' }}>
        {/* autoFocus signals the form opened even when it renders below the fold (ra-yuuv) */}
        <label>Variant ID:<br /><input autoFocus value={newVariant.id} onChange={upd('id')} placeholder="sat_inner" /></label>
        <label>Name:<br /><input value={newVariant.name} onChange={upd('name')} placeholder="Saturday Inner" /></label>
        <label>Day:<br />
          <select value={newVariant.day} onChange={upd('day')}>
            {DAY_ORDER.map((d) => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
          </select>
        </label>
        <label>Location:<br /><input value={newVariant.location} onChange={upd('location')} placeholder="inner" style={{ width: '80px' }} /></label>
        <label>VHF:<br /><input value={newVariant.vhf} onChange={upd('vhf')} placeholder="37" style={{ width: '50px' }} /></label>
        <label>Fleet:<br /><input value={newVariant.fleet} onChange={upd('fleet')} style={{ width: '60px' }} /></label>
        <label>Course Card:<br /><CourseCardSelect value={newVariant.course_card_id} onChange={upd('course_card_id')} courseCards={courseCards} style={{ width: '200px' }} /></label>
        <label>Doc Set ID:<br /><input value={newVariant.document_set_id} onChange={upd('document_set_id')} placeholder="hyc-sat" style={{ width: '120px' }} /></label>
        <button className="rt-btn-save" onClick={onCreate}>Create</button>
        <button className="rt-btn-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
